import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import api from "../../lib/api";
import { ShieldCheck, Users, Tag, LayoutDashboard, Package, Settings, Activity, Sparkles, Trash2, MessageCircle, Trophy, Image as ImageIcon, RotateCcw } from "lucide-react";
import { fmt } from "../../lib/money";

export default function AdminDashboard() {
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [recRoi, setRecRoi] = useState(null);
  const [storage, setStorage] = useState(null);
  const [recClicks, setRecClicks] = useState(null);
  const [bandit, setBandit] = useState(null);
  const [raffleTick, setRaffleTick] = useState(0);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/recommendations/analytics").then((r) => setRecRoi(r.data)).catch(() => {});
    api.get("/admin/storage/usage").then((r) => setStorage(r.data)).catch(() => {});
    api.get("/admin/recommendations/clicks?limit=25").then((r) => setRecClicks(r.data)).catch(() => {});
    api.get("/admin/recommendations/bandit").then((r) => setBandit(r.data)).catch(() => {});
  }, [location.pathname]);

  const navCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm ${isActive ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`;

  const isOverview = location.pathname === "/admin" || location.pathname === "/admin/";

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-[240px_1fr] bg-white">
      <aside className="bg-[#121212] text-white p-5 lg:sticky lg:top-16 lg:self-start lg:h-[calc(100vh-4rem)]">
        <div className="flex items-center gap-2 font-display font-black text-xl mb-8">
          <ShieldCheck className="w-5 h-5" /> Admin
        </div>
        <nav className="space-y-1 -mx-4">
          <NavLink end to="/admin" className={navCls} data-testid="admin-nav-overview">
            <LayoutDashboard className="w-4 h-4" /> Overview
          </NavLink>
          <NavLink to="/admin/users" className={navCls} data-testid="admin-nav-users">
            <Users className="w-4 h-4" /> Users
          </NavLink>
          <NavLink to="/admin/orders" className={navCls} data-testid="admin-nav-orders">
            <Package className="w-4 h-4" /> All orders
          </NavLink>
          <NavLink to="/admin/categories" className={navCls} data-testid="admin-nav-categories">
            <Tag className="w-4 h-4" /> Categories
          </NavLink>
          <NavLink to="/admin/trackmatrix" className={navCls} data-testid="admin-nav-trackmatrix">
            <Activity className="w-4 h-4" /> Trackmatrix
          </NavLink>
          <NavLink to="/admin/affiliates" className={navCls} data-testid="admin-nav-affiliates">
            <Sparkles className="w-4 h-4" /> Affiliates
          </NavLink>
          <NavLink to="/admin/deleted-orders" className={navCls} data-testid="admin-nav-deleted">
            <Trash2 className="w-4 h-4" /> Deleted orders
          </NavLink>
          <NavLink to="/admin/settings" className={navCls} data-testid="admin-nav-settings">
            <Settings className="w-4 h-4" /> Site settings
          </NavLink>
          <NavLink to="/admin/whatsapp" className={navCls} data-testid="admin-nav-whatsapp">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </NavLink>
          <NavLink to="/admin/pos" className={navCls} data-testid="admin-nav-pos">
            <Package className="w-4 h-4" /> POS catalog
          </NavLink>
        </nav>
      </aside>

      <div className="p-4 sm:p-6 lg:p-10 min-w-0">
        {isOverview ? (
          <div>
            <h1 className="font-display font-black text-4xl tracking-tight">Control room</h1>
            {stats && (
              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                {[
                  { k: "Users", v: stats.users },
                  { k: "Sellers", v: stats.sellers },
                  { k: "Pending", v: stats.pending_sellers },
                  { k: "Products", v: stats.products },
                  { k: "Orders", v: stats.orders },
                  { k: "Revenue", v: fmt(stats.revenue) },
                ].map((s) => (
                  <div key={s.k} className="border border-neutral-200 p-5" data-testid={`admin-stat-${s.k.toLowerCase()}`}>
                    <div className="text-xs uppercase tracking-widest text-neutral-500">{s.k}</div>
                    <div className="font-display font-black text-4xl mt-2">{s.v}</div>
                  </div>
                ))}
              </div>
            )}

            {storage && <StorageUsageCard data={storage} />}

            {recRoi && (
              <div className="mt-10" data-testid="admin-rec-roi">
                <div className="flex items-end justify-between mb-3">
                  <h2 className="font-display font-black text-2xl tracking-tight">Recommendations ROI</h2>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Driven by AI-ranked suggestions</div>
                </div>
                <div className="grid sm:grid-cols-4 border border-black">
                  {[
                    { label: "Add-to-cart clicks", value: recRoi.totals.adds, testid: "rec-stat-adds" },
                    { label: "Orders driven", value: recRoi.totals.orders, testid: "rec-stat-orders" },
                    { label: "Items sold", value: recRoi.totals.items_sold, testid: "rec-stat-items" },
                    { label: "Revenue", value: fmt(recRoi.totals.revenue), testid: "rec-stat-revenue" },
                  ].map((s, i) => (
                    <div key={s.label} data-testid={s.testid} className={`p-4 sm:p-5 ${i < 3 ? "sm:border-r border-black" : ""} ${i < 2 ? "border-r border-black" : ""} ${i < 2 ? "border-b sm:border-b-0" : ""} border-black`}>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{s.label}</div>
                      <div className="font-display font-black text-2xl mt-1">{s.value}</div>
                    </div>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  {[
                    { label: "Product page “You might also like”", key: "rec_product" },
                    { label: "Home “Recommended for you”", key: "rec_home" },
                  ].map((s) => {
                    const d = recRoi.by_source[s.key] || {};
                    return (
                      <div key={s.key} className="border border-neutral-200 p-4" data-testid={`rec-source-${s.key}`}>
                        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{s.label}</div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                          <div className="text-neutral-500">Adds</div><div className="font-display font-bold">{d.adds || 0}</div>
                          <div className="text-neutral-500">Orders</div><div className="font-display font-bold">{d.orders || 0}</div>
                          <div className="text-neutral-500">Items</div><div className="font-display font-bold">{d.items_sold || 0}</div>
                          <div className="text-neutral-500">Revenue</div><div className="font-display font-bold text-[#00A355]">{fmt(d.revenue || 0)}</div>
                          <div className="text-neutral-500">Conv rate</div><div className="font-display font-bold">{d.conversion_rate || 0}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {bandit && <BanditScoreboard data={bandit} />}

            {recClicks && recClicks.rows && recClicks.rows.length > 0 && (
              <RecClicksTable data={recClicks} />
            )}

            <RafflesPanel refreshKey={raffleTick} />

            <RaffleSetup onDone={() => setRaffleTick((t) => t + 1)} />

            <SharePreviewTester />
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
}

function StorageUsageCard({ data }) {
  const pct = Math.min(100, data.percent_used || 0);
  const danger = pct >= 85;
  const warn = pct >= 60 && pct < 85;
  const bar = danger ? "bg-red-600" : warn ? "bg-amber-500" : "bg-black";
  const label = danger
    ? "Nearly full — new image uploads will start failing. Delete old files or ask Emergent to raise the quota."
    : warn
    ? "Filling up — consider deleting unused product images soon."
    : "Plenty of room for new product images and videos.";
  const quotaGb = Math.round(((data.quota_mb || 5120) / 1024) * 10) / 10;
  return (
    <div className="mt-10" data-testid="admin-storage-usage">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display font-black text-2xl tracking-tight">Image & video storage</h2>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
          Emergent Object Storage
        </div>
      </div>
      <div className={`border p-5 ${danger ? "border-red-600 bg-red-50" : "border-black"}`}>
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="font-display font-black text-3xl tracking-tight" data-testid="storage-used">
            {data.total_mb} MB
            <span className="text-neutral-400 text-base font-bold ml-2">of {quotaGb} GB</span>
          </div>
          <div className="text-sm font-mono" data-testid="storage-percent">{pct.toFixed(1)}%</div>
        </div>
        <div className="mt-3 h-2 w-full bg-neutral-200 overflow-hidden">
          <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs text-neutral-600 mt-3">{label}</div>
        <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
          {["image", "video", "other"].map((k) => {
            const row = data.by_type?.[k];
            return (
              <div key={k} className="border border-neutral-200 p-3" data-testid={`storage-${k}`}>
                <div className="uppercase tracking-widest text-neutral-500 font-semibold">{k}</div>
                <div className="font-display font-bold text-lg mt-1">
                  {row ? `${row.mb} MB` : "—"}
                </div>
                <div className="text-neutral-500">
                  {row ? `${row.count} file${row.count === 1 ? "" : "s"}` : "0 files"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}



function BanditScoreboard({ data }) {
  const hotShare = data.hot?.share || 0;
  const aiShare = data.ai_pick?.share || 0;
  const leader = data.leader;
  const total = (data.hot?.clicks || 0) + (data.ai_pick?.clicks || 0);
  const Pill = ({ win }) => (
    <span
      className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ml-2 ${
        win ? "bg-black text-white" : "bg-neutral-200 text-neutral-500"
      }`}
    >
      {win ? "Leading" : "Losing"}
    </span>
  );
  return (
    <div className="mt-10" data-testid="admin-rec-bandit">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display font-black text-2xl tracking-tight">
          Rec strategy · learning
        </h2>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
          Last {data.window_days}d · {data.exploit_pct}% exploit / {data.explore_pct}% explore
        </div>
      </div>
      <div className="border border-black p-5">
        {data.cold_start ? (
          <div className="text-sm text-neutral-600" data-testid="bandit-cold-start">
            No clicks yet in the last {data.window_days} days — strategy is 50/50 random
            until shoppers start clicking. Keep this tab open; the bandit will tilt toward
            whichever row drives more clicks automatically.
          </div>
        ) : (
          <>
            <div className="text-xs text-neutral-600 mb-3" data-testid="bandit-leader">
              Currently serving <b>{leader === "hot" ? "Hot right now" : "Picked for you"}</b>{" "}
              {data.exploit_pct}% of the time, with {data.explore_pct}% going to the other
              so the row keeps learning.
            </div>
            <div className="h-3 w-full bg-neutral-200 overflow-hidden flex" data-testid="bandit-bar">
              <div
                className="h-full bg-[#FF2A00] transition-all"
                style={{ width: `${hotShare}%` }}
                title={`Hot ${hotShare}%`}
              />
              <div
                className="h-full bg-[#0033FF] transition-all"
                style={{ width: `${aiShare}%` }}
                title={`AI pick ${aiShare}%`}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div className="border border-neutral-200 p-3" data-testid="bandit-hot">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#FF2A00] mr-2" />
                  <div className="uppercase tracking-widest text-neutral-500 text-[11px] font-semibold">
                    Hot right now
                  </div>
                  <Pill win={leader === "hot"} />
                </div>
                <div className="font-display font-black text-2xl mt-1">
                  {data.hot?.clicks || 0}
                </div>
                <div className="text-xs text-neutral-500">
                  clicks ({hotShare}% of total)
                </div>
              </div>
              <div className="border border-neutral-200 p-3" data-testid="bandit-ai">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#0033FF] mr-2" />
                  <div className="uppercase tracking-widest text-neutral-500 text-[11px] font-semibold">
                    Picked for you
                  </div>
                  <Pill win={leader === "ai_pick"} />
                </div>
                <div className="font-display font-black text-2xl mt-1">
                  {data.ai_pick?.clicks || 0}
                </div>
                <div className="text-xs text-neutral-500">
                  clicks ({aiShare}% of total)
                </div>
              </div>
            </div>
            <div className="text-[11px] text-neutral-400 mt-3">
              Based on {total} click{total === 1 ? "" : "s"} in the last {data.window_days} days.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RecClicksTable({ data }) {
  const rows = data.rows || [];
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <div className="mt-10" data-testid="admin-rec-clicks">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display font-black text-2xl tracking-tight">
          Recommended-for-you · per-product clicks
        </h2>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
          Ranked by shopper clicks
        </div>
      </div>
      <div className="border border-black overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-black">
            <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-600">
              <th className="p-3">Product</th>
              <th className="p-3 text-right">Clicks</th>
              <th className="p-3 text-right hidden sm:table-cell">Home</th>
              <th className="p-3 text-right hidden sm:table-cell">Product page</th>
              <th className="p-3 text-right">Add-to-cart</th>
              <th className="p-3 text-right hidden md:table-cell">Views</th>
              <th className="p-3 text-right hidden md:table-cell">Conversions</th>
              <th className="p-3 text-right">Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const bar = Math.round((r.clicks / max) * 100);
              return (
                <tr
                  key={r.product_id}
                  className="border-t border-neutral-200 hover:bg-neutral-50"
                  data-testid={`rec-click-row-${r.product_id}`}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {r.image && (
                        <img
                          src={r.image}
                          alt=""
                          className="w-10 h-10 object-cover border border-neutral-200 flex-none"
                        />
                      )}
                      <div className="min-w-0">
                        <a
                          href={`/products/${r.product_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-display font-bold truncate hover:underline block"
                        >
                          {r.title}
                        </a>
                        <div className="text-[11px] text-neutral-500 truncate">
                          {r.seller_name} · {r.category || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right align-middle">
                    <div className="font-display font-black">{r.clicks}</div>
                    <div className="mt-1 h-1 w-24 bg-neutral-200 ml-auto overflow-hidden">
                      <div className="h-full bg-black" style={{ width: `${bar}%` }} />
                    </div>
                  </td>
                  <td className="p-3 text-right hidden sm:table-cell">{r.clicks_home}</td>
                  <td className="p-3 text-right hidden sm:table-cell">{r.clicks_product}</td>
                  <td className="p-3 text-right">{r.adds_to_cart}</td>
                  <td className="p-3 text-right hidden md:table-cell">{r.views}</td>
                  <td className="p-3 text-right hidden md:table-cell">{r.conversions}</td>
                  <td className="p-3 text-right font-mono">{r.score}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SharePreviewTester() {
  const [productId, setProductId] = useState("");
  const trimmed = productId.trim();
  const origin = typeof window !== "undefined" ? window.location.origin : "https://guyanadiscounts.com";
  const shareUrl = trimmed ? `${origin}/api/share/products/${trimmed}` : "";
  const fbDebug = trimmed
    ? `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(shareUrl)}`
    : "https://developers.facebook.com/tools/debug/";
  const twitterValidator = "https://cards-dev.twitter.com/validator";
  const linkedinInspector = "https://www.linkedin.com/post-inspector/";
  const whatsappWipe = trimmed
    ? `https://wa.me/?text=${encodeURIComponent(shareUrl)}`
    : "https://wa.me/";

  return (
    <div className="mt-10" data-testid="admin-share-preview">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display font-black text-2xl tracking-tight">Share preview tester</h2>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
          Verify WhatsApp / Facebook / Twitter previews
        </div>
      </div>
      <div className="border border-black p-4 bg-neutral-50">
        <label className="text-xs uppercase tracking-widest font-bold text-neutral-700">Product ID</label>
        <input
          data-testid="share-preview-input"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          placeholder="prod_xxxxxxxxxxxx (paste full id)"
          className="w-full mt-1 h-10 px-3 border border-black rounded-none font-mono text-sm"
        />
        {trimmed && (
          <div className="mt-3 text-xs">
            <div className="text-neutral-500 mb-1">Share-aware URL:</div>
            <code className="block bg-white border border-neutral-300 p-2 break-all">{shareUrl}</code>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <a
            data-testid="open-fb-debugger"
            href={fbDebug}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold border border-black ${trimmed ? "bg-black text-white hover:bg-neutral-800" : "bg-white text-black hover:bg-neutral-100"}`}
          >
            Facebook / WhatsApp debugger →
          </a>
          <a
            href={twitterValidator}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold border border-black bg-white text-black hover:bg-neutral-100"
          >
            Twitter validator →
          </a>
          <a
            href={linkedinInspector}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold border border-black bg-white text-black hover:bg-neutral-100"
          >
            LinkedIn inspector →
          </a>
          {trimmed && (
            <a
              href={whatsappWipe}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold border border-[#25D366] bg-[#25D366] text-white hover:bg-[#1eb558]"
            >
              Send to WhatsApp →
            </a>
          )}
        </div>
        <p className="text-xs text-neutral-500 mt-3">
          Paste a product ID, then open the Facebook debugger to fetch a fresh preview (also clears their cache).
          The same `/api/share/products/&lt;id&gt;` URL is what the Share button on each product page copies for sellers.
        </p>
      </div>
    </div>
  );
}


function RafflesPanel({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);

  const load = () => api.get("/admin/raffles/live")
    .then((r) => setData(r.data))
    .catch(() => setData({ raffles: [], history: [] }));

  useEffect(() => {
    let alive = true;
    const guarded = () => api.get("/admin/raffles/live")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ raffles: [], history: [] }));
    guarded();
    const t = setInterval(guarded, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [refreshKey]);

  const resetRaffle = async (code) => {
    if (!window.confirm(`Restart ${code}? Entries and the current winner are cleared.`)) return;
    try {
      await api.post(`/admin/discounts/${code}/raffle/reset`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Reset failed");
    }
  };

  const raffles = data?.raffles || [];
  const history = data?.history || [];
  if (!data || (raffles.length === 0 && history.length === 0)) return null;

  return (
    <div className="mt-10" data-testid="admin-raffles-panel">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
          <Trophy className="w-5 h-5" /> Raffles
        </h2>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Live — refreshes every 30s</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {raffles.map((r) => {
          const won = !!r.winner;
          const waPhone = (r.winner?.whatsapp || "").replace(/[^\d]/g, "");
          return (
            <div key={r.code} className={`border p-4 ${won ? "border-[#00A355] bg-[#00A355]/5" : "border-neutral-200"}`} data-testid={`raffle-tile-${r.code.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-black text-lg">{r.code}</div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`raffle-reset-${r.code.toLowerCase()}`}
                    onClick={() => resetRaffle(r.code)}
                    title="Restart raffle (clears entries + winner)"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${won ? "text-[#00A355]" : "text-neutral-500"}`} data-testid={`raffle-status-${r.code.toLowerCase()}`}>
                    {won ? "Won" : "Open"}
                  </div>
                </div>
              </div>
              <div className="text-sm text-neutral-600 mt-1 truncate">{r.product?.title || r.product?.product_id || "—"}</div>
              <div className="font-display font-black text-3xl mt-2" data-testid={`raffle-entries-${r.code.toLowerCase()}`}>
                {r.participants_count}<span className="text-base text-neutral-400 font-bold"> / {r.target_position} entries</span>
              </div>
              {r.entries && r.entries.length > 0 && (
                <div className="mt-3 border-t border-neutral-200 pt-2" data-testid={`raffle-entries-list-${r.code.toLowerCase()}`}>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Entries — tap a name to contact</div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.entries.map((e) => {
                      const k = `${r.code}:${e.position}`;
                      return (
                        <button
                          key={k}
                          data-testid={`raffle-entry-${r.code.toLowerCase()}-${e.position}`}
                          onClick={() => setOpenEntry(openEntry === k ? null : k)}
                          className={`text-xs font-semibold px-2 py-1 border ${openEntry === k ? "bg-black text-white border-black" : e.is_winner ? "border-[#00A355] text-[#00A355]" : "border-neutral-300 text-neutral-700 hover:border-black"}`}
                        >
                          #{e.position} {e.name}
                        </button>
                      );
                    })}
                  </div>
                  {openEntry && openEntry.startsWith(`${r.code}:`) && (() => {
                    const e = r.entries.find((x) => `${r.code}:${x.position}` === openEntry);
                    if (!e) return null;
                    const wa = (e.whatsapp || "").replace(/[^\d]/g, "");
                    const tpl = data?.raffle_message || "Hi {name}! Thanks for entering the {code} raffle on Guyana Discounts — you're entry #{position} for the {product}.";
                    const msg = tpl
                      .replace(/\{name\}/g, e.name)
                      .replace(/\{code\}/g, r.code)
                      .replace(/\{product\}/g, r.product?.title || "the prize")
                      .replace(/\{position\}/g, String(e.position));
                    return (
                      <div className="mt-2 border border-black p-3 bg-neutral-50 text-sm" data-testid={`raffle-entry-card-${r.code.toLowerCase()}-${e.position}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{e.name}</span>
                          <span className="font-mono text-xs text-neutral-600">{e.whatsapp || "no WhatsApp on file"}</span>
                        </div>
                        <div className="mt-2 text-xs text-neutral-600 italic border-l-2 border-neutral-300 pl-2 whitespace-pre-wrap">{msg}</div>
                        {wa && (
                          <a
                            data-testid={`raffle-entry-send-${r.code.toLowerCase()}-${e.position}`}
                            href={`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-2 h-9 px-3 text-xs font-semibold bg-[#25D366] text-white hover:bg-[#1eb558]"
                          >
                            <MessageCircle className="w-3.5 h-3.5" /> Send WhatsApp
                          </a>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {won && (
                <div className="mt-3 border-t border-[#00A355]/30 pt-3 text-sm" data-testid={`raffle-winner-${r.code.toLowerCase()}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#00A355] font-bold mb-1">
                    Winner — entry #{r.winner_position}
                  </div>
                  <div className="font-semibold">{r.winner.name || r.winner.email || "Unknown"}</div>
                  <div className="text-neutral-600">{r.winner.whatsapp || "no WhatsApp on file"}</div>
                  {waPhone && (
                    <a
                      data-testid={`raffle-contact-${r.code.toLowerCase()}`}
                      href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`🎉 Hi ${r.winner.name || ""}, you won the ${r.code} raffle on Guyana Discounts!`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-2 h-9 px-3 text-xs font-semibold bg-[#25D366] text-white hover:bg-[#1eb558]"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Contact winner
                    </a>
                  )}
                  {r.graphic_url && (
                    <a
                      data-testid={`raffle-graphic-${r.code.toLowerCase()}`}
                      href={r.graphic_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-2 ml-2 h-9 px-3 text-xs font-semibold border border-black bg-black text-white hover:bg-neutral-800"
                    >
                      <ImageIcon className="w-3.5 h-3.5" /> Winner graphic
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {history.length > 0 && (
        <div className="mt-6" data-testid="raffle-history">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Past winners</div>
          <div className="border border-neutral-200 divide-y divide-neutral-200">
            {history.map((h, i) => {
              const waPhone = (h.winner?.whatsapp || "").replace(/[^\d]/g, "");
              return (
                <div key={`${h.code}-${h.won_at}-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid={`raffle-history-row-${i}`}>
                  <span className="font-display font-black">{h.code}</span>
                  <span className="text-neutral-600 truncate">{h.product?.title || h.product_id || "—"}</span>
                  <span className="font-semibold">{h.winner?.name || h.winner?.email || "Unknown"}</span>
                  <span className="text-neutral-500">entry #{h.position}</span>
                  <span className="text-neutral-400 text-xs">{h.won_at ? new Date(h.won_at).toLocaleString() : ""}</span>
                  {h.graphic_url && (
                    <a
                      data-testid={`raffle-history-graphic-${i}`}
                      href={h.graphic_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0033FF] hover:underline"
                    >
                      <ImageIcon className="w-3.5 h-3.5" /> Graphic
                    </a>
                  )}
                  {waPhone && (
                    <a
                      data-testid={`raffle-history-contact-${i}`}
                      href={`https://wa.me/${waPhone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-[#1eb558] hover:underline"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> {h.winner.whatsapp}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function RaffleSetup({ onDone }) {
  const [code, setCode] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [product, setProduct] = useState(null);
  const [target, setTarget] = useState(10);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const needle = q.trim();
      if (product && needle === product.title) return;
      if (needle.length >= 4) {
        api.get(`/search/suggest?q=${encodeURIComponent(needle)}`)
          .then((r) => setResults(r.data.products || []))
          .catch(() => setResults([]));
      } else {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, product]);

  const enable = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const c = code.trim().toUpperCase();
      await api.post(`/admin/discounts/${c}/raffle`, {
        product_id: product.product_id,
        target_position: Number(target),
      });
      setMsg({ ok: true, text: `${c} is live — entry #${target} wins "${product.title}" free + free shipping.` });
      setProduct(null);
      setQ("");
      onDone?.();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Failed to enable raffle" });
    } finally {
      setBusy(false);
    }
  };

  const ready = code.trim().length > 0 && product && Number(target) >= 2;

  return (
    <div className="mt-10" data-testid="admin-raffle-setup">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display font-black text-2xl tracking-tight">Set up a raffle</h2>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Nth entry wins the item free</div>
      </div>
      <div className="border border-black p-4 bg-neutral-50">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-neutral-700">Discount code</label>
            <input
              data-testid="raffle-setup-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="JENNY"
              className="w-full mt-1 h-10 px-3 border border-black rounded-none font-mono text-sm uppercase"
            />
          </div>
          <div className="relative">
            <label className="text-xs uppercase tracking-widest font-bold text-neutral-700">Prize product</label>
            <input
              data-testid="raffle-setup-product"
              value={q}
              onChange={(e) => { setQ(e.target.value); setProduct(null); }}
              placeholder="Type 4+ letters to search…"
              className="w-full mt-1 h-10 px-3 border border-black rounded-none text-sm"
            />
            {results.length > 0 && !product && (
              <div className="absolute z-10 left-0 right-0 top-full bg-white border border-black divide-y divide-neutral-200 max-h-56 overflow-auto" data-testid="raffle-setup-results">
                {results.map((p) => (
                  <button
                    key={p.product_id}
                    data-testid={`raffle-setup-pick-${p.product_id}`}
                    onClick={() => { setProduct(p); setQ(p.title); setResults([]); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-100 flex items-center gap-2"
                  >
                    {p.image && <img src={p.image} alt="" className="w-8 h-8 object-cover border border-neutral-200" />}
                    <span className="truncate">{p.title}</span>
                    <span className="ml-auto text-xs text-neutral-500 shrink-0">{p.seller_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-neutral-700">Winning entry #</label>
            <input
              data-testid="raffle-setup-target"
              type="number"
              min={2}
              max={10000}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full mt-1 h-10 px-3 border border-black rounded-none text-sm"
            />
          </div>
        </div>
        {product && (
          <div className="mt-3 text-xs text-neutral-600" data-testid="raffle-setup-selected">
            Prize: <span className="font-semibold text-black">{product.title}</span> ({product.product_id})
          </div>
        )}
        <button
          data-testid="raffle-setup-enable"
          onClick={enable}
          disabled={!ready || busy}
          className={`mt-3 h-10 px-5 text-sm font-semibold ${ready && !busy ? "bg-black text-white hover:bg-neutral-800" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
        >
          {busy ? "Enabling…" : "Enable raffle"}
        </button>
        {msg && (
          <div className={`mt-3 text-sm font-semibold ${msg.ok ? "text-[#00A355]" : "text-red-600"}`} data-testid="raffle-setup-msg">
            {msg.text}
          </div>
        )}
        <p className="text-xs text-neutral-500 mt-3">
          The code must already exist (create it in the seller dashboard first). Enabling restricts the code to the prize
          product, clears any previous entries/winner, and makes the Nth entry get 100% off + free shipping. Entries 1 to N−1
          see “SORRY YOU HAVE NOT WON…”, everyone after N sees “someone has already won”.
        </p>
      </div>
    </div>
  );
}

